/**
 * 🔒 COMPOSANT : sellerie-app/frontend/src/components/Export/ExportButtons.tsx
 * Boutons d'export Excel/CSV avec verrou Premium (front-only).
 */

import React, { useState, useContext } from 'react';
import { ExportService } from '../../services/ExportService';
import AuthContext from '../../contexts/AuthContext';

interface ExportButtonsProps {
  devisId: string;
  devisReference: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary';
}

const ExportButtons: React.FC<ExportButtonsProps> = ({
  devisId,
  devisReference,
  className = '',
  size = 'md',
  variant = 'secondary'
}) => {
  const [loading, setLoading] = useState<'excel' | 'csv' | null>(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const { access } = useContext(AuthContext);
  const isPremium = access?.effective_plan === 'premium';
  const canExport = isPremium;

  const handleExportExcel = async () => {
    if (!canExport) {
      setShowPremiumModal(true);
      return;
    }
    try {
      setLoading('excel');
      const excelData = await ExportService.exporterExcel(devisId);
      const nomFichier = ExportService.genererNomFichier(devisReference, 'excel');
      ExportService.telechargerExcel(excelData, nomFichier);
    } catch (error) {
      console.error('❌ Erreur export Excel:', error);
      alert('Erreur lors de l’export Excel. Veuillez réessayer.');
    } finally {
      setLoading(null);
    }
  };

  const handleExportCSV = async () => {
    if (!canExport) {
      setShowPremiumModal(true);
      return;
    }
    try {
      setLoading('csv');
      const csvData = await ExportService.exporterCSV(devisId);
      const nomFichier = ExportService.genererNomFichier(devisReference, 'csv');
      ExportService.telechargerCSV(csvData, nomFichier);
    } catch (error) {
      console.error('❌ Erreur export CSV:', error);
      alert('Erreur lors de l’export CSV. Veuillez réessayer.');
    } finally {
      setLoading(null);
    }
  };

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const variantClasses = {
    primary: 'bg-indigo-600 hover:bg-indigo-700 text-white',
    secondary: 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'
  };

  const buttonClass = `
    inline-flex items-center
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    font-medium rounded-md
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
    disabled:opacity-50 disabled:cursor-not-allowed
    transition-colors duration-200
  `.trim();

  return (
    <div className={`flex space-x-2 ${className}`}>
      {!isPremium && (
        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-md border border-yellow-200">
          Premium
          <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c1.1 0 2-.9 2-2V7a2 2 0 10-4 0v2c0 1.1.9 2 2 2z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 11h14v8a2 2 0 01-2 2H7a2 2 0 01-2-2v-8z" />
          </svg>
        </span>
      )}

      {/* Bouton Excel */}
      <button
        onClick={handleExportExcel}
        disabled={!canExport || loading !== null}
        className={buttonClass}
        title={canExport ? "Télécharger le fichier Excel pour l'atelier" : "Export Excel réservé au Premium"}
      >
        {loading === 'excel' ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Export...
          </>
        ) : (
          <>
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Excel
          </>
        )}
      </button>

      {/* Bouton CSV */}
      <button
        onClick={handleExportCSV}
        disabled={!canExport || loading !== null}
        className={buttonClass}
        title={canExport ? "Télécharger le fichier CSV pour l'atelier" : "Export CSV réservé au Premium"}
      >
        {loading === 'csv' ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Export...
          </>
        ) : (
          <>
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            CSV
          </>
        )}
      </button>

      {/* Modal Premium */}
      {showPremiumModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Export Excel/CSV — Premium</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Téléchargez vos fichiers atelier (Excel/CSV) pour partager et préparer la découpe.
                </p>
              </div>
              <button onClick={() => setShowPremiumModal(false)} className="text-gray-400 hover:text-gray-600">
                <span className="sr-only">Fermer</span>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-3">
              <button
                className="w-full inline-flex justify-center items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition"
                onClick={() => { window.location.href = '/abonnement'; }}
              >
                Passer au Premium
              </button>
              <button
                className="w-full inline-flex justify-center items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                onClick={() => { window.location.href = '/abonnement'; }}
              >
                Voir les offres
              </button>
              <button
                className="w-full inline-flex justify-center items-center px-4 py-2 text-sm text-gray-500 hover:text-gray-700"
                onClick={() => setShowPremiumModal(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportButtons;
